import React from "react";
import {useTranslation} from "react-i18next";
import {makeRequest} from "../../services/requestService";
import {API_URL, COLLECTION_ROLE, DIGILIB_BASE_URL, DIGILIB_MANIFESTER_URL, DIGILIB_SCALER_URL} from "../../constants";
import {Link, useHistory, useParams} from "react-router-dom";
import {
    AppBar,
    Box,
    Breadcrumbs,
    Divider,
    Grid,
    IconButton,
    Paper,
    Slide,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import clsx from "clsx";
import {RENDER_MODES, useOpenSeaDragonViewer} from "../../components/OpenSeaDragonViewer/useOpenSeaDragonViewer";
import ViewerPagination from "../../components/ViewerPagination/ViewerPagination";
import {useRecoilState} from "recoil";
import {showNavigationState} from "../../components/Layout/Layout";
import CircularProgressWithLabel from "../../components/CircularProgress/CircularProgressWithLabel";
import DrawerGallery from "../../components/Gallery/DrawerGallery";
import TreeDataViewer from "../../components/TreeDataViewer/TreeDataViewer";
import OpenSeaDragonViewer from "../../components/OpenSeaDragonViewer/OpenSeaDragonViewer";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import PhotoSizeSelectSmallIcon from "@mui/icons-material/PhotoSizeSelectSmall";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import StorageIcon from "@mui/icons-material/Storage";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        minWidth: "100%",
    },
    flex: {
        flexGrow: 1,
    },
    fullScreen: {
        minHeight: "100vh",
    },
    centered: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    right: {
        display: "flex",
        flexDirection: "column",
        minWidth: "fit-content",
        minHeight: "150px",
    },
    left: {
        display: "flex",
        flexDirection: "column",
    },
    data: {
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "200px"
    },
    link: {
        textDecoration: "none",
        color: "inherit",
    },
    toggle: {
        "&:.MuiButtonBase-root": {
            color: "red"
        }
    }
}));

export default function Page() {
    const classes = useStyles();
    const {t} = useTranslation("common");
    const {id} = useParams();
    const theme = useTheme();
    const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'));
    const xsUp = useMediaQuery(theme => theme.breakpoints.up('sm'));
    const [showNav, setShowNav] = useRecoilState(showNavigationState);
    const [pageData, setPageData] = React.useState();
    const [role, setRole] = React.useState(COLLECTION_ROLE.READER);
    const [issueData, setIssueData] = React.useState();
    const [loadingValue, setLoadingValue] = React.useState(0);
    const [manifestURL, setManifestURL] = React.useState();
    const [scalerURL, setScalerURL] = React.useState()
    const [pagination, setPagination] = React.useState();
    const [drawer, setDrawer] = React.useState(false);
    const [textMode, setTextMode] = React.useState('structured');
    const [viewMode, setViewMode] = React.useState(false);
    const history = useHistory();
    // eslint-disable-next-line no-unused-vars
    const {
        viewer,
        initViewer,
        render,
        switchMode
    } = useOpenSeaDragonViewer("issue-osd", scalerURL, null);

    React.useEffect(() => {
        document.title = t("main.title") + " - " + t("main.pages.page.title");
        const fetchProducts = async () => {
            const rejected = (status) => {
                if (status !== 200) {
                    if (pageRequest.status === 400 || pageRequest.status > 403) {
                        if (pageRequest.status === 404)
                            history.replace("/error/notfound");
                        else
                            history.push("/error");
                    }
                    return true;
                }
                return false;
            }
            setLoadingValue(0);
            let pageRequest = await makeRequest(`${API_URL}/page/${id}`);
            if (rejected(pageRequest.status))
                return false;
            setPageData(pageRequest.data);
            setLoadingValue(40);
            let issueRequest = await makeRequest(`${API_URL}/issue/${pageRequest.data.issue}`);
            if (rejected(issueRequest.status))
                return false;
            setIssueData(issueRequest.data);
            setLoadingValue(60);
            const url = `${API_URL}/user/collection/${issueRequest.data.title.collection}`;
            let collectionRequest = await makeRequest(url);
            if (rejected(collectionRequest.status)) {
                return false;
            }
            setRole(collectionRequest.data.role);
            setLoadingValue(80);
            setScalerURL(`${DIGILIB_BASE_URL}${DIGILIB_SCALER_URL}/${issueRequest.data.title.location}!${pageRequest.data.viewing}/info.json`);
            setManifestURL(`${DIGILIB_BASE_URL}${DIGILIB_MANIFESTER_URL}/${issueRequest.data.title.location}/manifest.json`);
            let paginationRequest = await makeRequest(`${API_URL}/page/pagination?pageID=${id}&titleID=${issueRequest.data.title.id}`);
            if (rejected(paginationRequest.status))
                return false;
            setPagination(paginationRequest.data);
            return true;
        }
        fetchProducts().then((isLoaded) => {
            if (isLoaded)
                setLoadingValue(100);
        });
        setDrawer(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);


    const toggleDrawer = (open) => (event) => {
        setDrawer(open);
    };

    const handleChangePage = (event, newPage) => {
        history.push(`/page/${pagination.firstPageId + newPage - 1}`);
    };

    const handleChangeTreeMode = (event, newTextMode) => {
        if (newTextMode !== null)
            setTextMode(newTextMode);
    };

    const toggleViewMode = () => {
        let mode = !viewMode;
        setViewMode(mode);
        mode ? switchMode(RENDER_MODES.FLUID) : switchMode(RENDER_MODES.FULL);
    }

    if (!Boolean(pageData) || !Boolean(issueData) || !Boolean(pagination))
        return (<CircularProgressWithLabel label={`${loadingValue}%`} size={80} color={"secondary"}/>);

    //RENDER

    const treeView = (
        <Slide direction="right" in={loadingValue === 100}>
            <Grid item xs={12} sm={12} md={6} xl={6} display="flex">
                <Paper elevation={0} className={clsx(classes.data, classes.flex)}>
                    <Box sx={{
                        p: 1,
                        pr: 5,
                        position: "absolute",
                        width: "100%",
                    }}>
                        <TreeDataViewer
                            page={pageData}
                            issue={issueData}
                            viewMode={viewMode}
                            role={role}
                            renderOSD={render}
                            textMode={textMode}
                        />
                    </Box>
                </Paper>
            </Grid>
        </Slide>
    );

    const osdViewer = (
        <Slide direction="left" in={loadingValue === 100}>
            <Grid item xs={12} sm={12} md={6} xl={6} display="flex">
                <Paper elevation={0} className={clsx(classes.right, classes.flex)}>
                    <OpenSeaDragonViewer
                        viewer={viewer}
                        viewerID="issue-osd"
                        init={initViewer}
                    />
                </Paper>
            </Grid>
        </Slide>
    );

    const contentRender = () => {
        if (mdUp)
            return <>
                {treeView}
                {osdViewer}
            </>
        return <>
            {osdViewer}
            {treeView}
        </>
    }

    return (
        <div className={clsx(classes.root, classes.flex, !showNav && classes.fullScreen)}>
            <AppBar
                elevation={1}
                sx={{
                    backgroundColor: theme.palette.appBar.main,
                }}
                position="sticky"
            >
                <Toolbar>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <Tooltip title={t("main.components.drawer.allpages")}>
                            <IconButton onClick={toggleDrawer(true)} color="inherit">
                                <ViewComfyIcon/>
                            </IconButton>
                        </Tooltip>
                        {xsUp && <>
                            <Breadcrumbs
                                aria-label="breadcrumb"
                                color="inherit"
                            >
                                <Link to={`/`}
                                      onClick={() => setShowNav(true)}
                                      className={classes.link}>
                                    <Typography variant="body1">{t("main.pages.home.title")}</Typography>
                                </Link>
                                <Link to={`/title/${issueData.title.id}`}
                                      onClick={() => setShowNav(true)}
                                      className={clsx(classes.link)}>
                                    <Typography variant="body2">{issueData.title.name}</Typography>
                                </Link>
                                <Typography variant="body2">{pageData.viewing}</Typography>
                            </Breadcrumbs>
                            <Tooltip title={issueData.title.description} arrow>
                                <InfoRoundedIcon/>
                            </Tooltip>
                        </>}
                        {!xsUp &&
                            <Link to={`/`}
                                  onClick={() => setShowNav(true)}
                                  className={classes.link}>
                                <Typography variant="body1">{t("main.pages.home.title")}</Typography>
                            </Link>}
                    </Stack>
                    <Box sx={{flexGrow: 1, minWidth: theme => theme.spacing(1)}}/>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <ToggleButton
                            size="small"
                            value="clip"
                            selected={viewMode}
                            onChange={toggleViewMode}
                        >
                            <Tooltip
                                title={viewMode ? t("main.components.dataViewer.appbar.unclip") : t("main.components.dataViewer.appbar.clip")}>
                                <PhotoSizeSelectSmallIcon size="small"/>
                            </Tooltip>
                        </ToggleButton>
                        <Divider orientation="vertical" flexItem/>
                        <ToggleButtonGroup size="small" value={textMode} exclusive onChange={handleChangeTreeMode}>
                            <ToggleButton value="structured">
                                <Tooltip title={t("main.components.dataViewer.appbar.structdata")}>
                                    <AccountTreeIcon fontSize="small"/>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="raw">
                                <Tooltip title={t("main.components.dataViewer.appbar.rawdata")}>
                                    <StorageIcon fontSize="small"/>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Divider orientation="vertical" flexItem/>
                        <ViewerPagination
                            count={pagination.totalPages}
                            page={pagination.currentPage}
                            onChange={handleChangePage}
                        />
                        <Tooltip
                            title={showNav ? t("main.components.dataViewer.appbar.hide")
                                : t("main.components.dataViewer.appbar.show")}>
                            <IconButton onClick={() => setShowNav(!showNav)} color="inherit">
                                {showNav ? <FullscreenIcon/> : <FullscreenExitIcon/>}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Toolbar>
            </AppBar>
            <DrawerGallery
                openHandler={toggleDrawer(true)}
                closeHandler={toggleDrawer(false)}
                open={drawer}
                manifestURL={manifestURL}
            />
            <Box m={2} display="flex" flexDirection="column" flexGrow={1} alignItems="center" justifyContent="center">
                {loadingValue < 100 &&
                    <CircularProgressWithLabel label={`${loadingValue}%`} size={80} color={"secondary"}/>}
                {loadingValue === 100 &&
                    <Grid container flexGrow={1} direction="row" spacing={2}>
                        {contentRender()}
                    </Grid>}
            </Box>
        </div>
    );
}